'use client';

import type React from 'react';
import { useState, useEffect } from 'react';
import AnimatedCounter from './animated-counter';

const MoreIcon = () => (
  <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
    <path d="M10 6a2 2 0 110-4 2 2 0 010 4zM10 12a2 2 0 110-4 2 2 0 010 4zM10 18a2 2 0 110-4 2 2 0 010 4z" />
  </svg>
);

const TrendUpIcon = () => (
  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={2}
      d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6"
    />
  </svg>
);

const TrendDownIcon = () => (
  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={2}
      d="M13 17h8m0 0V9m0 8l-8-8-4 4-6-6"
    />
  </svg>
);

interface MetricCardProps {
  title: string;
  value: number | string;
  change?: number;
  trend?: 'up' | 'down';
  icon?: React.ReactNode;
  delay?: number;
  tone?: 'primary' | 'success' | 'warning' | 'danger' | 'info';
  hint?: React.ReactNode;
}

export default function MetricCard({
  title,
  value,
  change,
  trend = 'up',
  icon,
  delay = 0,
  tone = 'primary',
  hint,
}: MetricCardProps) {
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => setIsVisible(true), delay);
    return () => clearTimeout(timer);
  }, [delay]);

  const toneMap: Record<string, string> = {
    primary: "bg-brand-50 text-brand-600 dark:bg-brand-900/30 dark:text-brand-400",
    success: "bg-green-50 text-green-600 dark:bg-green-900/30 dark:text-green-400",
    warning: "bg-yellow-50 text-yellow-600 dark:bg-yellow-900/30 dark:text-yellow-400",
    danger: "bg-red-50 text-red-600 dark:bg-red-900/30 dark:text-red-400",
    info: "bg-sky-50 text-sky-600 dark:bg-sky-900/30 dark:text-sky-400",
  };

  const positive = trend === 'up';

  return (
    <div
      className={`rounded-2xl p-5 shadow-sm border transform transition-all duration-700 hover:shadow-md
        bg-white border-gray-200
        dark:bg-gray-900 dark:border-gray-800
        ${isVisible ? 'translate-y-0 opacity-100' : 'translate-y-4 opacity-0'}
      `}
    >
      <div className="flex items-start justify-between">
        <div className={`h-10 w-10 rounded-xl flex items-center justify-center ${toneMap[tone]}`}>
          {icon || <span className="h-5 w-5 rounded-full bg-current opacity-20" />}
        </div>
        {change !== undefined && (
          <span
            className={`inline-flex items-center gap-1 text-xs font-medium px-2 py-1 rounded-full ${
              positive 
                ? "bg-green-50 text-green-600 dark:bg-green-900/30 dark:text-green-400" 
                : "bg-red-50 text-red-600 dark:bg-red-900/30 dark:text-red-400"
            }`}
          >
            {positive ? <TrendUpIcon /> : <TrendDownIcon />}
            {Math.abs(change)}%
          </span>
        )}
      </div>
      <div className="mt-4 text-sm text-gray-500 dark:text-gray-400">{title}</div>
      <div className="mt-1 text-2xl font-semibold tracking-tight text-gray-900 dark:text-white">
        {typeof value === 'number' ? <AnimatedCounter value={value} /> : value}
      </div>
      {hint && <div className="mt-1 text-xs text-gray-500 dark:text-gray-400">{hint}</div>}
    </div>
  );
}
